// Imports
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { t } from "i18next";
import { CONST } from "models/constants";
import { shallowEqual, useSelector } from "react-redux";
import { accountSiteList } from "store/appSelectors";
import { formatDate } from "util/datelib";
import { getAlertStateColor } from "util/status";
import MoreAction, { DropDownItem } from "widgets/controls/moreAction";
import Badge from "widgets/shared/badge";
import React from "react";

// predefined filter on state = OPEN
// State color coded OPEN: red, CLOSED: green, rest:yellow
// Show: snr, siteName, escalation level, created date, last interaction date, state

// React component for site cell that can use hooks
const SiteCell: React.FC<{ classId: string }> = ({ classId }) => {
  const sites = useSelector(accountSiteList, shallowEqual);
  const site = sites.find((a) => a.id === classId);
  return (
    <span className="flex flex-grow flex-row gap-1 truncate">
      {site ? site.name : classId}
    </span>
  );
};

// Define the structure of the JdTableModel
export class AlertsPageTableModel {
  closedDate: string;
  lastInteractionDate: string;
  state: string;
  level: number;
  snr: string;
  type: string;
  container: string;
  classId: string;
  config: {
    enabled: boolean;
  };
  timeline: {
    username: string;
    notifications: string;
    data: string;
    action: string;
    comment: string;
    created: string;
  }[];
  created: string;
  escalationDate: string;
  id: string;
  actions: string;
}

const defineAlertsTablecolumns = (
  onViewClick?: (info: CellContext<AlertsPageTableModel, any>) => void
) => {
  // Initialize the translation hook and
  const moreMenuItems: DropDownItem[] = [
    {
      label: t("button.action.view"),
      active: true,
      onSelected: onViewClick,
      permissionRole: CONST.ROLE_MANAGER,
    },
  ];
  // create a column helper for the JdTableModel
  const columnHelper = createColumnHelper<AlertsPageTableModel>();

  // Define the columns for the JdTable
  const defineAlertsTablecolumns = [
    // Description column
    columnHelper.accessor("snr", {
      enableSorting: true,
      header: () => <span className="">{t("table.header.alert.snr")}</span>,
      cell: (info) => <span className="">{info.getValue()}</span>,
      meta: { rowClick: true },
    }),

    columnHelper.accessor("classId", {
      enableSorting: false,
      meta: { rowClick: true },
      header: () => <span className="">{t("table.header.alert.site")}</span>,
      cell: (info): JSX.Element => {
        return <SiteCell classId={info.row.original.classId} />;
      },
    }),

    columnHelper.accessor("level", {
      enableSorting: false,
      header: () => (
        <span className="">{t("table.header.alert.escalation.level")}</span>
      ),
      cell: (info) => (
        <span className="">
          {
            <Badge
              label={info.getValue().toString()}
              color={"gray"}
              variant={"fill"}
              rounded={"rounded-full"}
            />
          }
        </span>
      ),
      meta: {
        rowClick: true,
        customClass: "justify-items-center",
      },
    }),

    columnHelper.accessor("created", {
      enableSorting: true,
      header: () => (
        <span className="">{t("table.header.alert.created.date")}</span>
      ),
      cell: (info) => (
        <span className="">{formatDate(info.getValue(), "lastcomms")}</span>
      ),
      meta: { rowClick: true },
    }),

    columnHelper.accessor("lastInteractionDate", {
      enableSorting: true,
      header: () => (
        <span className="">
          {t("table.header.alert.last.interaction.date")}
        </span>
      ),
      cell: (info) => (
        <span className="">{formatDate(info.getValue(), "lastcomms")}</span>
      ),
      meta: { rowClick: true },
    }),

    columnHelper.accessor("state", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.alert.state")}</span>,
      cell: (info) => (
        <span className="">
          {
            <Badge
              label={info.getValue()}
              color={getAlertStateColor(info.getValue())}
            />
          }
        </span>
      ),
      meta: {
        rowClick: true,
      },
    }),

    // Actions column
    columnHelper.accessor("actions", {
      enableSorting: false,
      meta: {
        rowClick: false,
        customClass: "justify-items-center",
        customHeadClass: "justify-items-center",
      },

      header: () => <span className="">{t("table.header.actions")}</span>,
      cell: (info) => (
        <div>
          <MoreAction
            items={moreMenuItems}
            info={info}
            title={t("button.action.alert.options")}
            extra="w-full flex"
          />
        </div>
      ),
    }),
  ];

  return defineAlertsTablecolumns;
};

export default defineAlertsTablecolumns;
